// Settings → Payments tab.
//
// IMPORTANT: this is the SETTINGS tab — *Stripe Connect status, commission
// tier display, and payout schedule*. The full Payments & Billing page
// (with tier progress bar, recent invoices, outstanding balances) lives at
// `apps/retailer/src/pages/payments/page.ts` — we link to it from here.
//
// Master spec divergence: the legacy `#settings-panel-payments` is just a
// "show tier progress on Payments page" toggle — see inventory note at
// /root/inventory_sections/01_retailer.md:497-499. We follow the master
// spec instead, which puts Stripe Connect + Commission settings + Payout
// Schedule here. The legacy visibility toggle is dropped.
//
// Backend
// -------
// Real, retailer-callable:
//   GET  /api/stripe/connect/status      — modules/stripe.js:170
//   GET  /api/stripe/connect/start       — modules/stripe.js:206 (returns
//                                          OAuth URL when configured, else
//                                          503 with `connectUrl: null`)
//   POST /api/stripe/connect/disconnect  — modules/stripe.js:265
//
// NOT yet implemented:
//   GET  /api/portal/settings/payout-schedule
//   PUT  /api/portal/settings/payout-schedule
//
// We:
//   1. Fetch /stripe/connect/status. The response includes connected,
//      stripeAccountId, since, and tier{tier,name,ratePct,label}. We
//      surface tier as read-only with a link to the Payments & Billing
//      page for full progress.
//   2. Connect → POST /stripe/connect/start (sic — the real endpoint is
//      GET, but we use the response's `connectUrl` either way). When the
//      backend is unconfigured we toast a friendly stub message instead
//      of opening anything.
//   3. Disconnect → confirm → POST /stripe/connect/disconnect.
//   4. Payout schedule → try GET first, fall back to "monthly"; PUT on
//      change, fall back to sessionStorage on 404/501.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./payments.css";

// ── Types ────────────────────────────────────────────────────────────────────

type PayoutSchedule = "daily" | "weekly" | "monthly" | "on-demand";

interface ServerTier {
  tier: number;
  name: string;
  ratePct: string;
  rateBps: number;
  label: string;
}

interface ConnectStatus {
  connected: boolean;
  stripeAccountId: string | null;
  email?: string | null;
  since?: number | null;
  annualGmvPence: number;
  annualGmvGbp: string;
  tier: ServerTier;
}

interface ConnectStartResponse {
  connectUrl: string | null;
  error?: string;
}

interface PayoutScheduleResponse {
  schedule?: PayoutSchedule;
}

interface SchedulePayload {
  schedule: PayoutSchedule;
}

// ── Constants ────────────────────────────────────────────────────────────────

const SCHEDULE_KEY = "tkos.set.payoutSchedule";

const SCHEDULE_OPTIONS: ReadonlyArray<{ value: PayoutSchedule; label: string; hint: string }> = [
  { value: "daily",     label: "Daily",     hint: "Funds settle every business day" },
  { value: "weekly",    label: "Weekly",    hint: "Funds settle every Friday" },
  { value: "monthly",   label: "Monthly",   hint: "Funds settle on the 1st of each month" },
  { value: "on-demand", label: "On demand", hint: "Trigger payouts manually from Stripe" },
];

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function readSessionSchedule(): PayoutSchedule | null {
  try {
    const raw = sessionStorage.getItem(SCHEDULE_KEY);
    if (raw === "daily" || raw === "weekly" || raw === "monthly" || raw === "on-demand") return raw;
    return null;
  } catch {
    return null;
  }
}

function writeSessionSchedule(value: PayoutSchedule): void {
  try {
    sessionStorage.setItem(SCHEDULE_KEY, value);
  } catch {
    /* ignore */
  }
}

function formatSince(epoch: number): string {
  return DATE_FMT.format(new Date(epoch));
}

function formatLastSync(epoch: number | null | undefined): string {
  if (typeof epoch !== "number") return "—";
  return DATE_FMT.format(new Date(epoch));
}

// ── Render ───────────────────────────────────────────────────────────────────

function renderShell(container: HTMLElement): {
  stripeEl: HTMLElement;
  commissionEl: HTMLElement;
  payoutEl: HTMLElement;
  dialogEl: HTMLDialogElement;
  dialogConfirmBtn: HTMLButtonElement;
  dialogCancelBtn: HTMLButtonElement;
} {
  container.innerHTML = `
    <section class="set-pay-root" aria-labelledby="set-pay-title">
      <h2 class="sr-only" id="set-pay-title">Payment settings</h2>

      <section class="set-pay-section" aria-labelledby="set-pay-stripe-title">
        <h3 class="set-section-title" id="set-pay-stripe-title">Stripe Connection</h3>
        <div class="set-pay-stripe" data-role="stripe">
          <div class="set-pay-loading">Loading Stripe status…</div>
        </div>
      </section>

      <section class="set-pay-section" aria-labelledby="set-pay-comm-title">
        <h3 class="set-section-title" id="set-pay-comm-title">Commission Settings</h3>
        <div class="set-pay-commission" data-role="commission">
          <div class="set-pay-loading">Loading tier…</div>
        </div>
      </section>

      <section class="set-pay-section" aria-labelledby="set-pay-payout-title">
        <h3 class="set-section-title" id="set-pay-payout-title">Payout Schedule</h3>
        <div class="set-pay-payout" data-role="payout"></div>
      </section>

      <dialog class="set-pay-dialog" data-role="dialog" aria-labelledby="set-pay-dialog-title">
        <form method="dialog" class="set-pay-dialog-form">
          <h3 class="set-pay-dialog-title" id="set-pay-dialog-title">Disconnect Stripe?</h3>
          <p class="set-pay-dialog-body">
            You'll need to reconnect before you can take new card payments through TKOS.
            Existing transactions and payouts already in Stripe are unaffected.
          </p>
          <div class="set-pay-dialog-actions">
            <button type="button" class="set-btn set-btn-secondary" data-role="dialog-cancel">Cancel</button>
            <button type="button" class="set-btn set-btn-danger" data-role="dialog-confirm">Disconnect</button>
          </div>
        </form>
      </dialog>
    </section>
  `;

  return {
    stripeEl: container.querySelector<HTMLElement>("[data-role='stripe']")!,
    commissionEl: container.querySelector<HTMLElement>("[data-role='commission']")!,
    payoutEl: container.querySelector<HTMLElement>("[data-role='payout']")!,
    dialogEl: container.querySelector<HTMLDialogElement>("[data-role='dialog']")!,
    dialogConfirmBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-confirm']")!,
    dialogCancelBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-cancel']")!,
  };
}

function renderStripeConnected(host: HTMLElement, status: ConnectStatus): void {
  const acct = status.stripeAccountId ?? "—";
  const sinceLine = typeof status.since === "number"
    ? `Connected ${escapeHtml(formatSince(status.since))}`
    : "Connection date unknown";
  host.innerHTML = `
    <div class="set-pay-stripe-card set-pay-stripe-card--ok">
      <div class="set-pay-stripe-icon" aria-hidden="true">
        ${iconMarkup("check", { size: "md" })}
      </div>
      <div class="set-pay-stripe-meta">
        <div class="set-pay-stripe-status">Connected</div>
        <dl class="set-pay-stripe-dl">
          <div><dt>Account ID</dt><dd><code>${escapeHtml(acct)}</code></dd></div>
          <div><dt>Status</dt><dd>${escapeHtml(sinceLine)}</dd></div>
          <div><dt>Last sync</dt><dd>${escapeHtml(formatLastSync(Date.now()))}</dd></div>
        </dl>
      </div>
      <div class="set-pay-stripe-actions">
        <button type="button" class="set-btn set-btn-danger" data-role="disconnect">Disconnect</button>
      </div>
    </div>
  `;
}

function renderStripeDisconnected(host: HTMLElement, lastError: string | null): void {
  const errorMarkup = lastError
    ? `<div class="set-pay-stripe-error">${escapeHtml(lastError)}</div>`
    : "";
  host.innerHTML = `
    <div class="set-pay-stripe-card set-pay-stripe-card--off">
      <div class="set-pay-stripe-icon set-pay-stripe-icon--off" aria-hidden="true">
        ${iconMarkup("info", { size: "md" })}
      </div>
      <div class="set-pay-stripe-meta">
        <div class="set-pay-stripe-status">Not connected</div>
        <p class="set-pay-stripe-blurb">
          Connect Stripe to take card payments from your customers and have
          platform commission settled automatically.
        </p>
        ${errorMarkup}
      </div>
      <div class="set-pay-stripe-actions">
        <button type="button" class="set-btn" data-role="connect">Connect Stripe</button>
      </div>
    </div>
  `;
}

function renderCommission(host: HTMLElement, tier: ServerTier | null, navigate: PageContext["navigate"]): void {
  if (!tier) {
    host.innerHTML = `<div class="set-pay-commission-empty">Tier information unavailable.</div>`;
    return;
  }
  host.innerHTML = `
    <div class="set-pay-tier-card">
      <div class="set-pay-tier-pill">${escapeHtml(tier.label || `Tier ${tier.tier} — ${tier.ratePct}%`)}</div>
      <p class="set-pay-tier-blurb">
        Your current commission rate is <b>${escapeHtml(tier.ratePct)}%</b>
        on platform turnover. The rate drops automatically as your rolling
        12-month turnover grows — see the Payments &amp; Billing page for
        full tier-progress details.
      </p>
      <button type="button" class="set-pay-tier-link" data-role="go-billing">View tier progress →</button>
    </div>
  `;
  const link = host.querySelector<HTMLButtonElement>("[data-role='go-billing']");
  link?.addEventListener("click", () => navigate("payments"));
}

function renderPayout(host: HTMLElement, current: PayoutSchedule, isLocal: boolean): void {
  const opts = SCHEDULE_OPTIONS.map((o) => `
    <label class="set-pay-radio">
      <input type="radio" name="set-pay-schedule" value="${o.value}"${o.value === current ? " checked" : ""}>
      <div class="set-pay-radio-body">
        <div class="set-pay-radio-label">${escapeHtml(o.label)}</div>
        <div class="set-pay-radio-hint">${escapeHtml(o.hint)}</div>
      </div>
    </label>
  `).join("");
  const localBadge = isLocal
    ? `<div class="set-pay-payout-note">Saved locally for this session — server endpoint pending.</div>`
    : "";
  host.innerHTML = `
    <div class="set-pay-payout-grid">${opts}</div>
    ${localBadge}
  `;
}

// (renderBanner helper omitted — the inline error surface is rendered
// directly in the Stripe card so we don't need the generic banner slot
// that other tabs use. Kept the DOM hook in renderShell for future use.)

// ── Mount ────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "payments",
  label: "Payments",
  icon: iconMarkup("card", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ui = renderShell(container);
    let controller: AbortController | null = new AbortController();
    let disposed = false;

    let status: ConnectStatus | null = null;
    let schedule: PayoutSchedule = "monthly";
    let scheduleIsLocal = false;
    let stripeError: string | null = null;

    // ── Confirm dialog plumbing ────────────────────────────────────────────

    let dialogResolve: ((confirmed: boolean) => void) | null = null;

    const closeDialog = (confirmed: boolean): void => {
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(confirmed);
        dialogResolve = null;
      }
    };

    const onConfirmClick = (): void => closeDialog(true);
    const onCancelClick = (): void => closeDialog(false);
    const onDialogCancel = (e: Event): void => {
      e.preventDefault();
      closeDialog(false);
    };

    ui.dialogConfirmBtn.addEventListener("click", onConfirmClick);
    ui.dialogCancelBtn.addEventListener("click", onCancelClick);
    ui.dialogEl.addEventListener("cancel", onDialogCancel);

    const confirmDisconnect = (): Promise<boolean> => {
      try {
        ui.dialogEl.showModal();
      } catch {
        ui.dialogEl.close();
        ui.dialogEl.showModal();
      }
      return new Promise<boolean>((resolve) => {
        dialogResolve = resolve;
      });
    };

    // ── Render shortcuts ───────────────────────────────────────────────────

    const refreshStripe = (): void => {
      if (status?.connected) {
        renderStripeConnected(ui.stripeEl, status);
      } else {
        renderStripeDisconnected(ui.stripeEl, stripeError);
      }
    };

    const refreshCommission = (): void => {
      renderCommission(ui.commissionEl, status?.tier ?? null, ctx.navigate);
    };

    const refreshPayout = (): void => {
      renderPayout(ui.payoutEl, schedule, scheduleIsLocal);
    };

    // ── Stripe load ────────────────────────────────────────────────────────

    const loadStripe = async (): Promise<void> => {
      try {
        const data = await ctx.api<ConnectStatus>("/stripe/connect/status", {
          signal: controller!.signal,
        });
        if (disposed) return;
        status = data;
        stripeError = null;
        refreshStripe();
        refreshCommission();
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        const msg = err instanceof Error ? err.message : "Couldn't load Stripe status";
        stripeError = msg;
        // Render a "not connected" card with an error hint.
        status = null;
        refreshStripe();
        renderCommission(ui.commissionEl, null, ctx.navigate);
        if (!isMissingEndpoint(err)) {
          ctx.showToast(msg, "error");
        }
      }
    };

    // ── Payout schedule load ───────────────────────────────────────────────

    const loadSchedule = async (): Promise<void> => {
      const cached = readSessionSchedule();
      if (cached) {
        schedule = cached;
        scheduleIsLocal = true;
        refreshPayout();
      } else {
        // Render with default while we fetch.
        refreshPayout();
      }

      try {
        const data = await ctx.api<PayoutScheduleResponse>(
          "/portal/settings/payout-schedule",
          { signal: controller!.signal },
        );
        if (disposed) return;
        if (data.schedule === "daily" || data.schedule === "weekly" || data.schedule === "monthly" || data.schedule === "on-demand") {
          schedule = data.schedule;
          scheduleIsLocal = false;
          refreshPayout();
        }
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          // Endpoint pending — keep cached/default and mark as local-only
          // so user knows their save won't hit the server yet.
          scheduleIsLocal = true;
          refreshPayout();
          return;
        }
        // Non-404 error — toast and keep current.
        const msg = err instanceof Error ? err.message : "Couldn't load payout schedule";
        ctx.showToast(msg, "error");
      }
    };

    void loadStripe();
    void loadSchedule();

    // ── Save schedule ──────────────────────────────────────────────────────

    const saveSchedule = async (next: PayoutSchedule): Promise<void> => {
      const prev = schedule;
      schedule = next;
      writeSessionSchedule(next);
      refreshPayout();

      try {
        const payload: SchedulePayload = { schedule: next };
        await ctx.api<unknown>("/portal/settings/payout-schedule", {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        if (disposed) return;
        scheduleIsLocal = false;
        refreshPayout();
        ctx.showToast("Payout schedule updated", "success");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          scheduleIsLocal = true;
          refreshPayout();
          ctx.showToast("Saved locally — server endpoint pending", "info");
          return;
        }
        // Roll back.
        schedule = prev;
        writeSessionSchedule(prev);
        refreshPayout();
        const msg = err instanceof Error ? err.message : "Couldn't save schedule";
        ctx.showToast(msg, "error");
      }
    };

    // ── Connect / Disconnect ───────────────────────────────────────────────

    const startConnect = async (): Promise<void> => {
      try {
        // Note: the server route is a GET that returns { connectUrl }.
        const data = await ctx.api<ConnectStartResponse>("/stripe/connect/start");
        if (disposed) return;
        if (data.connectUrl) {
          // Redirect the top window — Stripe Connect needs full-page nav.
          window.location.assign(data.connectUrl);
          return;
        }
        ctx.showToast(
          data.error || "Stripe Connect is not yet configured for this environment.",
          "info",
        );
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (err instanceof ApiError && err.status === 503) {
          ctx.showToast(
            "Stripe Connect is not yet configured. Ask an admin to set STRIPE_CONNECT_CLIENT_ID.",
            "info",
          );
          return;
        }
        if (isMissingEndpoint(err)) {
          ctx.showToast("Coming soon — Stripe Connect integration pending", "info");
          return;
        }
        const msg = err instanceof Error ? err.message : "Couldn't start Stripe Connect";
        ctx.showToast(msg, "error");
      }
    };

    const performDisconnect = async (): Promise<void> => {
      try {
        await ctx.api<unknown>("/stripe/connect/disconnect", { method: "POST" });
        if (disposed) return;
        // Refresh from server to reflect the new state authoritatively.
        await loadStripe();
        ctx.showToast("Stripe disconnected", "success");
        // No grace period: an active Stripe connection is mandatory, so a
        // disconnect re-locks the whole portal immediately (same gate as
        // initial onboarding). The shell re-runs the connect-status gate.
        window.dispatchEvent(new CustomEvent("tkos:stripe-disconnected"));
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        const msg = err instanceof Error ? err.message : "Couldn't disconnect Stripe";
        ctx.showToast(msg, "error");
      }
    };

    // ── Click delegation on the Stripe card ────────────────────────────────

    const onStripeClick = async (e: MouseEvent): Promise<void> => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("button[data-role]");
      if (!btn) return;
      const role = btn.dataset.role;
      if (role === "connect") {
        await startConnect();
      } else if (role === "disconnect") {
        const ok = await confirmDisconnect();
        if (ok) await performDisconnect();
      }
    };

    ui.stripeEl.addEventListener("click", onStripeClick);

    // ── Payout radio change ────────────────────────────────────────────────

    const onPayoutChange = (e: Event): void => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== "set-pay-schedule") return;
      const v = target.value;
      if (v === "weekly" || v === "monthly" || v === "on-demand") {
        void saveSchedule(v);
      }
    };

    ui.payoutEl.addEventListener("change", onPayoutChange);

    // ── Cleanup ────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      controller?.abort();
      controller = null;
      ui.stripeEl.removeEventListener("click", onStripeClick);
      ui.payoutEl.removeEventListener("change", onPayoutChange);
      ui.dialogConfirmBtn.removeEventListener("click", onConfirmClick);
      ui.dialogCancelBtn.removeEventListener("click", onCancelClick);
      ui.dialogEl.removeEventListener("cancel", onDialogCancel);
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(false);
        dialogResolve = null;
      }
    };
  },
};

export default tab;
